import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Modal from "@shared/v2/Modal";
import Button from "@shared/v2/Button";
import TextInput from "@shared/v2/TextInput";
import ProfilePhotoUpload from "@shared/v2/ProfilePhotoUpload";
import Dropdown from "@shared/v2/Dropdown";
import TextButton from "@shared/v2/TextButton";

const PreferredLenderModal = ({ open, onClose, onDelete, preferredLender: propsLender }) => {
  const [preferredLender, setPreferredLender] = useState(propsLender);

  useEffect(() => {
    setPreferredLender(propsLender);
  }, [propsLender]);

  return (
    <Modal
      show={open}
      onHide={onClose}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[640px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
    >
      <Modal.Header
        title={`${preferredLender?.id ? "Edit" : "Add"} Preferred Lender`}
        description="Preferred lenders are displayed on property details pages near the payment breakdown and calculator"
        closeButton
      />
      <Modal.Body className="tw-flex-1 tw-flex tw-flex-col tw-gap-[24px] tw-overflow-auto">
        {!preferredLender?.id && (
          <>
            <Dropdown
              label="Import Person Details"
              placeholder="Search for an existing team member to auto-populate the info below"
              helperText="Already added a lender to your team? Import their details here! Please note that you will still need to manually update details even after importing."
              options={[]}
              isSearchable
            />
            <div className="tw-bg-gray-10 tw-h-[1px] tw-w-full" />
          </>
        )}
        <div className="tw-flex tw-gap-[24px]">
          <TextInput
            containerClassName="tw-flex-1"
            label="First Name"
            isRequired
            value={preferredLender?.firstName || ""}
            onChange={({ target: { value } }) => setPreferredLender((p) => ({ ...p, firstName: value }))}
          />
          <TextInput
            containerClassName="tw-flex-1"
            label="Last Name"
            isRequired
            value={preferredLender?.lastName || ""}
            onChange={({ target: { value } }) => setPreferredLender((p) => ({ ...p, lastName: value }))}
          />
        </div>
        <div className="tw-flex tw-gap-[24px]">
          <TextInput
            containerClassName="tw-flex-1"
            label="Email Address"
            isRequired
            value={preferredLender?.emailAddress || ""}
            onChange={({ target: { value } }) => setPreferredLender((p) => ({ ...p, emailAddress: value }))}
          />
          <TextInput
            containerClassName="tw-flex-1"
            label="Phone Number"
            isRequired
            value={preferredLender?.phoneNumber || ""}
            onChange={({ target: { value } }) => setPreferredLender((p) => ({ ...p, phoneNumber: value }))}
          />
        </div>

        <div className="tw-flex tw-gap-[24px]">
          <div className="tw-flex-1 *:tw-w-full">
            <ProfilePhotoUpload
              label="Lender Photo"
              src={preferredLender?.photo}
              alt={`${preferredLender?.firstName || ""} ${preferredLender?.lastName || ""}`.trim()}
              onDelete={() => setPreferredLender((p) => ({ ...p, photo: null }))}
              onUpload={(file) => {
                if (file) {
                  const reader = new FileReader();
                  reader.addEventListener("load", () => {
                    setPreferredLender((p) => ({ ...p, photo: reader.result }));
                  });
                  reader.readAsDataURL(file);
                }
              }}
            />
          </div>
          <span className="tw-flex-1" />
        </div>

        <TextInput
          label="Job Title"
          value={preferredLender?.jobTitle || ""}
          onChange={({ target: { value } }) => setPreferredLender((p) => ({ ...p, jobTitle: value }))}
        />
        <TextInput
          label="Website Link"
          placeholder="Provide a link to this lender’s website or pre-qualification application"
          value={preferredLender?.websiteLink || ""}
          onChange={({ target: { value } }) => setPreferredLender((p) => ({ ...p, websiteLink: value }))}
        />
      </Modal.Body>
      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
        {preferredLender?.id ? (
          <TextButton size="medium" schema="warning" onClick={onDelete}>
            Delete Lender
          </TextButton>
        ) : (
          <Button size="medium" schema="tertiary" onClick={onClose}>
            Cancel
          </Button>
        )}
        <Button size="medium">{preferredLender?.id ? "Save" : "Add Lender"}</Button>
      </Modal.Footer>
    </Modal>
  );
};

PreferredLenderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  preferredLender: PropTypes.shape(),
};

PreferredLenderModal.defaultProps = {
  onDelete: null,
  preferredLender: null,
};

export default PreferredLenderModal;
